import { isCmpUiSuppressed } from './isCmpUiSuppressed';
import { pushConsentToDataLayer } from './pushConsentToDataLayer';
import type { OsanoConsent } from './types';

function getOsano() {
  return window.Osano;
}

function readConsent(): OsanoConsent | undefined {
  try {
    return getOsano()?.cm?.getConsent?.();
  } catch {
    return undefined;
  }
}

function applyConsentFromCmp() {
  if (isCmpUiSuppressed()) {
    return;
  }

  pushConsentToDataLayer(readConsent());
}

let started = false;

/**
 * Wire Osano consent to the dataLayer (GTM / Consent Mode).
 * Call once from `gatsby-browser` after `mixpanel.init()`.
 *
 * Note: Mixpanel is classified Essential (IN2-4538) and is no longer gated on
 * Osano consent; its opt state is handled entirely in `mixpanel.init()`.
 */
export function bootstrapOsanoIntegrations() {
  if (typeof window === 'undefined' || started) {
    return;
  }
  started = true;

  if (isCmpUiSuppressed()) {
    return;
  }

  const Osano = getOsano();
  if (!Osano) {
    // Stub queue is created synchronously by gatsby-ssr.jsx; if it's missing
    // here, the Osano script was likely blocked or removed. Surface it so we
    // can spot lost consent sync in Sentry/console logs rather than silently
    // leaving GTM stuck on deny defaults.
    // eslint-disable-next-line no-console
    console.warn(
      '[osano] CMP queue not found on window; consent sync disabled.',
    );
    return;
  }

  const run = () => {
    if (!getOsano()?.cm?.getConsent) {
      return;
    }
    applyConsentFromCmp();
  };

  Osano('onInitialized', run);
  Osano('onConsentSaved', run);

  run();
}

/**
 * Reset the module-level singleton so each test starts from a clean slate.
 * Not part of the production API.
 */
export function __resetForTests() {
  started = false;
}
